import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '../config/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Shield, Upload, Plus, Trash2, FileText, Image, ChevronDown, AlertTriangle, CheckCircle, Clock, XCircle, RefreshCw, Lock } from 'lucide-react';

const STATUS_BADGES = {
  pending: { label: 'Pending Review', icon: Clock, className: 'bg-amber-50 text-amber-700 border-amber-200' },
  verified: { label: 'Verified', icon: CheckCircle, className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  rejected: { label: 'Rejected', icon: XCircle, className: 'bg-red-50 text-red-700 border-red-200' },
  expired: { label: 'Expired', icon: AlertTriangle, className: 'bg-slate-50 text-slate-600 border-slate-200' },
  needs_resubmission: { label: 'Resubmission Needed', icon: RefreshCw, className: 'bg-orange-50 text-orange-700 border-orange-200' },
};

export default function CredentialsManager() {
  const [credentials, setCredentials] = useState([]);
  const [typeConfigs, setTypeConfigs] = useState({});
  const [typeOptions, setTypeOptions] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedType, setSelectedType] = useState('');
  const [formFields, setFormFields] = useState({});
  const [attachments, setAttachments] = useState({});
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fetchCredentials = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/tradespeople/credentials`, { withCredentials: true });
      setCredentials(res.data);
    } catch (e) { /* ignore */ }
  }, []);

  const fetchTypeConfigs = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/tradespeople/credential-types`);
      setTypeOptions(res.data.types || []);
      setTypeConfigs(res.data.configs || {});
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { fetchCredentials(); fetchTypeConfigs(); }, [fetchCredentials, fetchTypeConfigs]);

  const config = typeConfigs[selectedType] || null;

  const handleTypeChange = (type) => {
    setSelectedType(type);
    setFormFields({});
    setAttachments({});
  };

  const handleFieldChange = (key, value) => {
    setFormFields(prev => ({ ...prev, [key]: value }));
  };

  const handleFileUpload = async (key, file) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', 'credentials');
      const res = await axios.post(`${BACKEND_URL}/api/uploads/document`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = res.data.url || res.data.file_url;
      setAttachments(prev => ({ ...prev, [key]: { url, name: file.name, type: file.type } }));
      toast.success(`${file.name} uploaded`);
    } catch (e) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedType || !config) return;
    setSubmitting(true);
    try {
      // Merge attachment URLs into fields for backend
      const fields = { ...formFields };
      for (const [key, att] of Object.entries(attachments)) {
        fields[key + '_url'] = att.url;
        fields[key + '_name'] = att.name;
      }
      await axios.post(`${BACKEND_URL}/api/tradespeople/credentials`, {
        credential_type: selectedType,
        fields,
      }, { withCredentials: true });
      toast.success('Credential submitted for verification');
      setShowForm(false);
      setSelectedType('');
      setFormFields({});
      setAttachments({});
      fetchCredentials();
    } catch (e) {
      const errors = e.response?.data?.detail?.errors;
      if (errors) {
        toast.error(errors.join(', '));
      } else {
        toast.error(e.response?.data?.detail || 'Submission failed');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (credId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/tradespeople/credentials/${credId}`, { withCredentials: true });
      toast.success('Credential removed');
      fetchCredentials();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const renderFieldInput = (fd) => {
    const val = formFields[fd.key] || fd.default || '';
    if (fd.type === 'select') {
      return (
        <select
          value={val}
          onChange={(e) => handleFieldChange(fd.key, e.target.value)}
          className="w-full px-3 py-2 rounded-lg border border-slate-300 bg-white text-sm focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
          data-testid={`cred-field-${fd.key}`}
        >
          <option value="">Select...</option>
          {(fd.options || []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      );
    }
    if (fd.type === 'dob') {
      // Date of birth — DD/MM/YYYY UI canonical, normalised server-side.
      // Auto-insert slashes as the user types numeric digits.
      const handleDobChange = (raw) => {
        const digits = raw.replace(/\D/g, '').slice(0, 8);
        let formatted = digits;
        if (digits.length > 4) {
          formatted = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
        } else if (digits.length > 2) {
          formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`;
        }
        handleFieldChange(fd.key, formatted);
      };
      const dobInvalid = val && val.length === 10 && !/^\d{2}\/\d{2}\/\d{4}$/.test(val);
      return (
        <div className="space-y-1">
          <input
            type="text"
            inputMode="numeric"
            value={val}
            placeholder={fd.placeholder || 'DD/MM/YYYY'}
            onChange={(e) => handleDobChange(e.target.value)}
            maxLength={10}
            className={`w-full px-3 py-2 rounded-lg border text-sm focus:ring-2 focus:border-orange-400 focus:ring-orange-400 ${dobInvalid ? 'border-red-300 bg-red-50' : 'border-slate-300'}`}
            data-testid={`cred-field-${fd.key}`}
            autoComplete="off"
          />
          {fd.helper_text && (
            <p className="text-xs text-slate-500" data-testid={`cred-field-${fd.key}-helper`}>
              {fd.helper_text}
            </p>
          )}
        </div>
      );
    }
    if (fd.type === 'date') {
      return (
        <input
          type="date"
          value={val}
          onChange={(e) => handleFieldChange(fd.key, e.target.value)}
          className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
          data-testid={`cred-field-${fd.key}`}
        />
      );
    }
    return (
      <input
        type="text"
        value={val}
        placeholder={fd.placeholder || ''}
        onChange={(e) => handleFieldChange(fd.key, e.target.value)}
        className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
        data-testid={`cred-field-${fd.key}`}
      />
    );
  };

  // Re-usable label that highlights private fields (e.g. date of birth)
  // with a lock icon so the trade can clearly see this value won't reach
  // employers.
  const renderFieldLabel = (fd, required) => (
    <label className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
      {fd.private && (
        <Lock
          className="h-3.5 w-3.5 text-slate-400"
          data-testid={`cred-field-${fd.key}-private-icon`}
          aria-label="Private — visible only to you and admins"
        />
      )}
      <span>{fd.label}</span>
      {required && <span className="text-red-500">*</span>}
    </label>
  );

  const renderAttachmentUpload = (att, required) => {
    const current = attachments[att.key];
    return (
      <div key={att.key} className="space-y-1.5">
        <label className="text-sm font-medium text-slate-700">
          {att.label} {required && <span className="text-red-500">*</span>}
        </label>
        {current ? (
          <div className="flex items-center gap-2 p-2 bg-emerald-50 border border-emerald-200 rounded-lg">
            {current.type?.startsWith('image/') ? <Image className="h-4 w-4 text-emerald-600" /> : <FileText className="h-4 w-4 text-emerald-600" />}
            <span className="text-sm text-emerald-700 truncate flex-1">{current.name}</span>
            <button onClick={() => setAttachments(prev => { const n = {...prev}; delete n[att.key]; return n; })} className="text-red-400 hover:text-red-600">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : (
          <label className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-orange-400 hover:bg-orange-50/50 transition-colors" data-testid={`cred-upload-${att.key}`}>
            <Upload className="h-4 w-4 text-slate-400" />
            <span className="text-sm text-slate-500">Click to upload</span>
            <input
              type="file"
              accept={att.accept}
              className="hidden"
              onChange={(e) => handleFileUpload(att.key, e.target.files[0])}
            />
          </label>
        )}
      </div>
    );
  };

  // Group type options by category
  const cardTypes = typeOptions.filter(t => t.category === 'card');
  const certTypes = typeOptions.filter(t => t.category === 'certificate');

  return (
    <div className="space-y-4" data-testid="credentials-manager">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <Shield className="h-5 w-5 text-orange-500" />
          Credentials & Qualifications
        </h3>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-1.5 px-3 py-2 bg-orange-500 text-white text-sm font-medium rounded-lg hover:bg-orange-600 transition-colors"
          data-testid="add-credential-btn"
        >
          <Plus className="h-4 w-4" /> Add Credential
        </button>
      </div>

      {/* Existing credentials list */}
      {credentials.length === 0 ? (
        <div className="text-center py-8 text-slate-500 text-sm">
          No credentials submitted yet. Add your first credential to get verified.
        </div>
      ) : (
        <div className="space-y-3">
          {credentials.map(cred => {
            const status = cred.verification_status || cred.status || 'pending';
            const badge = STATUS_BADGES[status] || STATUS_BADGES.pending;
            const BadgeIcon = badge.icon;
            const cfg = typeConfigs[cred.credential_type];
            const fields = cred.fields || {};
            const numberLabel = cfg?.number_label || 'Number';
            const numberField = fields[cfg?.number_label === 'Card Number' ? 'card_number' : 'certificate_reference'] || cred.credential_number || '-';

            return (
              <div key={cred.credential_id} className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`credential-card-${cred.credential_id}`}>
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-900 text-sm">{cfg?.label || cred.credential_type}</span>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border ${badge.className}`}>
                        <BadgeIcon className="h-3 w-3" /> {badge.label}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600">{numberLabel}: {numberField}</p>
                    {fields.expiry_date && <p className="text-xs text-slate-400">Expires: {fields.expiry_date}</p>}
                    {cred.rejection_reason && (
                      <p className="text-xs text-red-600 mt-1">Reason: {cred.rejection_reason.replace(/_/g, ' ')}</p>
                    )}
                  </div>
                  <button
                    onClick={() => handleDelete(cred.credential_id)}
                    className="text-slate-400 hover:text-red-500 transition-colors"
                    data-testid={`delete-cred-${cred.credential_id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add Credential Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Credential</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Type selector */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-slate-700">Credential Type <span className="text-red-500">*</span></label>
              <div className="relative">
                <select
                  value={selectedType}
                  onChange={(e) => handleTypeChange(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-300 bg-white text-sm appearance-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
                  data-testid="cred-type-select"
                >
                  <option value="">Select credential type...</option>
                  <optgroup label="Card-Based">
                    {cardTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </optgroup>
                  <optgroup label="Certificates & Qualifications">
                    {certTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </optgroup>
                </select>
                <ChevronDown className="h-4 w-4 text-slate-400 absolute right-3 top-3 pointer-events-none" />
              </div>
            </div>

            {/* Dynamic fields based on selected type */}
            {config && (
              <>
                {/* Required fields */}
                {config.required_fields.map(fd => (
                  <div key={fd.key} className="space-y-1.5">
                    {renderFieldLabel(fd, true)}
                    {renderFieldInput(fd)}
                  </div>
                ))}

                {/* Optional fields */}
                {config.optional_fields.length > 0 && (
                  <div className="pt-2 border-t border-slate-100">
                    <p className="text-xs text-slate-400 mb-2">Optional fields</p>
                    {config.optional_fields.map(fd => (
                      <div key={fd.key} className="space-y-1.5 mb-3">
                        {renderFieldLabel(fd, false)}
                        {renderFieldInput(fd)}
                      </div>
                    ))}
                  </div>
                )}

                {/* Attachments */}
                <div className="pt-2 border-t border-slate-100 space-y-3">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Attachments</p>
                  {(config.required_attachments || []).map(att => renderAttachmentUpload(att, true))}
                  {(config.optional_attachments || []).map(att => renderAttachmentUpload(att, false))}
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <button onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!selectedType || submitting || uploading}
              className="px-4 py-2 bg-orange-500 text-white text-sm font-medium rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="submit-credential-btn"
            >
              {submitting ? 'Submitting...' : 'Submit for Verification'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
